import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE = `${window.location.origin}/api`;

const Page = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px 80px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 800;
`;

const Search = styled.input`
  height: 42px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 12px;
  outline: none;
  min-width: 260px;
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 12px;
  outline: none;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin: 10px 0 18px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#111" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111")};
  cursor: pointer;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat( auto-fill, minmax(260px, 1fr) );
  gap: 16px;
`;

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  border: 1px solid #eef0f2;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0,0,0,0.05);
  transition: transform .15s ease, box-shadow .15s ease;

  &:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); }
`;

const Cover = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const Body = styled.div`
  padding: 12px 14px 14px;
`;

const Row = styled.div`
  display: flex; gap: 10px; align-items: center; justify-content: space-between;
`;

const Name = styled.h3`
  margin: 0; font-size: 1.05rem; font-weight: 800;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $active }) => ($active ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.12)")};
  color: ${({ $active }) => ($active ? "#065f46" : "#9b1c1c")};
  border: 1px solid ${({ $active }) => ($active ? "rgba(16,185,129,.25)" : "rgba(239,68,68,.25)")};
`;

const Meta = styled.div`
  display: flex; gap: 10px; color: #6b7280; font-size: 13px; margin-top: 6px; flex-wrap: wrap;
`;

const Empty = styled.div`
  padding: 30px; text-align: center; color: #6b7280; border: 1px dashed #e5e7eb; border-radius: 12px;
`;

// i18n
const dict = {
  ka: {
    pageTitle: "კოლექციები | Shopaholics",
    h1: "ყველა კოლექცია",
    search: "ძებნა…",
    tabs: { all: "ყველა", active: "აქტიური", inactive: "არააქტიური" },
    sort: { label: "სორტი", recent: "ახლები", oldest: "ძველები", az: "A–Z", items: "პროდუქტებით" },
    items: (n)=>`${n} ნივთი`,
    active: "აქტიური",
    inactive: "არააქტიური",
    created: "შექმნილი",
    view: "ნახვა",
  },
  en: {
    pageTitle: "Collections | Shopaholics",
    h1: "All Collections",
    search: "Search…",
    tabs: { all: "All", active: "Active", inactive: "Inactive" },
    sort: { label: "Sort", recent: "Newest", oldest: "Oldest", az: "A–Z", items: "By items" },
    items: (n)=>`${n} items`,
    active: "Active",
    inactive: "Inactive",
    created: "Created",
    view: "View",
  }
};

const prettyDate = (s, lang='en')=>{
  const d = s ? new Date(s) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang==='ka'?'ka-GE':'en-GB', { year:'numeric', month:'short', day:'2-digit' });
};

export default function CollectionsDirectory(){
  const { language } = useLanguage();
  const t = dict[language] || dict.en;

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');   // all | active | inactive
  const [sort, setSort] = useState('recent');    // recent | oldest | az | items
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{ document.title = t.pageTitle; }, [language]);

  useEffect(()=>{
    let cancelled = false;
    const controller = new AbortController();

    (async ()=>{
      try{
        setLoading(true);
        const res = await api.get(`${API_BASE}/collections/all.php`, {
          signal: controller.signal,
          params: { q, status, sort, _: Date.now() },
          withCredentials: false,
          headers: { Accept:'application/json' }
        });
        if (cancelled) return;
        setRows(Array.isArray(res.data) ? res.data : []);
      }catch(e){
        if (!cancelled) setRows([]);
        console.error('collections fetch error', e?.message || e);
      }finally{
        if (!cancelled) setLoading(false);
      }
    })();

    return ()=>{ cancelled = true; controller.abort(); };
  }, [q, status, sort]);

  const list = useMemo(()=>rows, [rows]);

  return (
    <Page>
      <HeaderRow>
        <Title>{t.h1}</Title>

        <Search
          placeholder={t.search}
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />

        <Select value={sort} onChange={(e)=>setSort(e.target.value)}>
          <option value="recent">{t.sort.recent}</option>
          <option value="oldest">{t.sort.oldest}</option>
          <option value="az">{t.sort.az}</option>
          <option value="items">{t.sort.items}</option>
        </Select>
      </HeaderRow>


      {loading ? (
        <Grid>
          {Array.from({length: 8}).map((_,i)=>(
            <div key={i} style={{
              height: 240, borderRadius:14, border:'1px solid #eef0f2', background:
              'linear-gradient(90deg,#f6f7f8 25%, #edeef1 37%, #f6f7f8 63%)',
              backgroundSize:'400% 100%', animation:'shimmer 1.4s ease infinite'
            }}/>
          ))}
          <style>{`@keyframes shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
        </Grid>
      ) : list.length === 0 ? (
        <Empty>—</Empty>
      ) : (
        <Grid>
          {list.map(c=>(
            <Card to={`/collections/${c.slug}`} key={c.id} aria-label={c.slug}>
              <Cover src={c.cover_image || '/images/placeholder/collection-cover.jpg'} alt={c.slug}/>
              <Body>
                <Row>
                  <Name>{c.name_ka || c.name_en || c.slug}</Name>
                  <Badge $active={!!c.is_active}>
                    {c.is_active ? t.active : t.inactive}
                  </Badge>
                </Row>
                <Meta>
                  <span>{dict[language].items(Number(c.product_count)||0)}</span>
                  {c.created_at && <span>• {t.created}: {prettyDate(c.created_at, language)}</span>}
                </Meta>
              </Body>
            </Card>
          ))}
        </Grid>
      )}
    </Page>
  );
}
